import { z } from 'zod/v3';
import {
  SecurityQuestionnaireStatusSchema,
  QuestionnaireAnswerStatusSchema,
  SecurityQuestionnaireSourceSchema,
} from '../generated/enums';

// ========== #988: Grounded AI security-questionnaire answering ==========

// Re-export the auto-generated Prisma enum schemas under the *Enum names callers
// use. Source of truth is schema.prisma → regenerated by `db:generate`. Never
// hand-write a parallel z.enum([...]) here (drifts from Prisma — see issue #476).
export {
  SecurityQuestionnaireStatusSchema as SecurityQuestionnaireStatusEnum,
  QuestionnaireAnswerStatusSchema as QuestionnaireAnswerStatusEnum,
  SecurityQuestionnaireSourceSchema as SecurityQuestionnaireSourceEnum,
} from '../generated/enums';
export type {
  SecurityQuestionnaireStatus,
  QuestionnaireAnswerStatus,
  SecurityQuestionnaireSource,
} from '../generated/enums';

/**
 * A single grounded citation backing a drafted answer. `kind` identifies which
 * ground-truth surface the fact came from; `refId` is that record's id; `quote`
 * is the supporting snippet. Stored as `QuestionnaireQuestion.draftCitations`.
 */
export const QuestionnaireCitationKindSchema = z.enum([
  'policy', // approved PolicyDocument
  'claim', // verified PolicyClaim
  'test', // APPROVED ControlEvidenceStatus
  'evidence_pack', // completed EvidencePack
  'source', // RAG source chunk
  'library', // a previously approved answer-library entry (cross-questionnaire reuse)
]);
export type QuestionnaireCitationKind = z.infer<typeof QuestionnaireCitationKindSchema>;

export const QuestionnaireCitationSchema = z.object({
  kind: QuestionnaireCitationKindSchema,
  refId: z.string(),
  label: z.string(),
  quote: z.string().nullable().optional(),
  submissionId: z.string().optional(),
  versionId: z.string().optional(),
  documentId: z.string().optional(),
  sourceId: z.string().optional(),
  chunkId: z.string().optional(),
});
export type QuestionnaireCitation = z.infer<typeof QuestionnaireCitationSchema>;

/**
 * Best-effort mapping from the uploaded file's columns to question fields,
 * discovered at parse time. The founder can correct it inline (no mandatory
 * wizard). Stored as `SecurityQuestionnaire.columnMapping`.
 */
export const QuestionnaireColumnMappingSchema = z.object({
  questionColumn: z.string(),
  answerColumn: z.string().nullable().optional(),
  sectionColumn: z.string().nullable().optional(),
  sheetName: z.string().nullable().optional(),
  headerRowIndex: z.number().int().nonnegative().default(0),
});
export type QuestionnaireColumnMapping = z.infer<typeof QuestionnaireColumnMappingSchema>;

/**
 * One sheet's detected columns, surfaced by `questionnaire.previewSheets` so the
 * founder can correct a mis-detected / FAILED column mapping in the review panel.
 */
export const QuestionnaireSheetPreviewSchema = z.object({
  sheetName: z.string(),
  headers: z.array(z.string()),
  suggested: QuestionnaireColumnMappingSchema.nullable(),
});
export type QuestionnaireSheetPreview = z.infer<typeof QuestionnaireSheetPreviewSchema>;

export const QuestionnaireConfidenceSchema = z.enum(['high', 'medium', 'low']);
export type QuestionnaireConfidence = z.infer<typeof QuestionnaireConfidenceSchema>;

/** Minimal teammate identity for rendering a question's assignee (#1369). */
export const QuestionnaireAssigneeDTOSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  email: z.string().nullable(),
  image: z.string().nullable(),
});
export type QuestionnaireAssigneeDTO = z.infer<typeof QuestionnaireAssigneeDTOSchema>;

export const QuestionnaireAnswerScopeSchema = z.object({
  service: z.string().trim().min(1).max(1000),
  environment: z.string().trim().max(1000).nullable(),
  customerRelationship: z.enum(['unknown', 'prospective', 'contracted']),
});
export type QuestionnaireAnswerScope = z.infer<typeof QuestionnaireAnswerScopeSchema>;

/** Question semantics, shared by drafting, diagnostics and the evaluation runner. */
export const QuestionnaireRequirementPlanSchema = z.object({
  requirements: z.array(z.object({
    id: z.string().min(1),
    sourceText: z.string().min(1),
    query: z.string().min(1),
    intent: z.enum(['document_exists', 'current_operation', 'specific_value', 'capability', 'commitment', 'description']),
    condition: z.string().nullable(),
  })).min(1).max(12),
  groups: z.array(z.object({
    mode: z.enum(['all', 'any']),
    requirementIds: z.array(z.string()).min(1),
  })).min(1).max(12),
});
export type QuestionnaireRequirementPlan = z.infer<typeof QuestionnaireRequirementPlanSchema>;

export const QuestionnaireRequirementAssessmentSchema = z.object({
  requirementId: z.string(),
  outcome: z.enum(['supported', 'partial', 'missing', 'conflict', 'needs_scope', 'not_applicable']),
  claim: z.string(),
  factIds: z.array(z.string()),
  evidenceMode: z.enum(['policy_requirement', 'explicit_current', 'explicit_negative', 'none'])
    .describe('Classify the assertion, not the file type: requires/must/shall is policy_requirement; declarative present practice is explicit_current even in a policy document.'),
  extractedValue: z.string().nullable().describe('For specific_value, the exact requested value or interval supported by the facts. Null when the value is unknown; absence of documentation is not a value.'),
  claimScopeValid: z.boolean(),
  explanation: z.string(),
  missingInformation: z.string(),
  followupQuery: z.string().nullable(),
});
export type QuestionnaireRequirementAssessment = z.infer<typeof QuestionnaireRequirementAssessmentSchema>;

/** Latest drafting attempt, separate from the human review lifecycle. */
export const QuestionnaireDraftDiagnosticsSchema = z.object({
  runId: z.string(),
  outcome: z.enum(['complete', 'partial', 'needs_info', 'error']),
  stage: z.enum(['planning', 'retrieval', 'evaluation', 'generation', 'validation', 'library']),
  reason: z.string(),
  explanation: z.string(),
  missingRequirements: z.array(z.string()),
  candidateFactIds: z.array(z.string()),
  supportedFactIds: z.array(z.string()),
  modelId: z.string(),
  strategyVersion: z.number().int().positive(),
  answerScope: QuestionnaireAnswerScopeSchema.nullable().optional(),
  plan: QuestionnaireRequirementPlanSchema.optional(),
  assessments: z.array(QuestionnaireRequirementAssessmentSchema).optional(),
  retrievalRounds: z.number().int(),
});
export type QuestionnaireDraftDiagnostics = z.infer<typeof QuestionnaireDraftDiagnosticsSchema>;

export const QuestionnaireQuestionDTOSchema = z.object({
  id: z.string(),
  questionnaireId: z.string(),
  workspaceId: z.string(),
  rowIndex: z.number().int(),
  questionText: z.string(),
  section: z.string().nullable(),
  answerType: z.string().nullable(),
  draftAnswer: z.string().nullable(),
  draftCitations: z.array(QuestionnaireCitationSchema).nullable(),
  draftConfidence: QuestionnaireConfidenceSchema.nullable(),
  draftDiagnostics: QuestionnaireDraftDiagnosticsSchema.nullable().optional(),
  finalAnswer: z.string().nullable(),
  status: QuestionnaireAnswerStatusSchema,
  reviewedBy: z.string().nullable(),
  reviewedAt: z.coerce.date().nullable(),
  /** Teammate this question is routed to (#1369). */
  assigneeId: z.string().nullable(),
  assignee: QuestionnaireAssigneeDTOSchema.nullable(),
  /** Resolved identity of who approved/reviewed (from reviewedBy) — for provenance. */
  reviewer: QuestionnaireAssigneeDTOSchema.nullable(),
  answerLibraryEntryId: z.string().nullable(),
  duplicateOfId: z.string().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type QuestionnaireQuestionDTO = z.infer<typeof QuestionnaireQuestionDTOSchema>;

export const SecurityQuestionnaireDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  programId: z.string().nullable(),
  title: z.string(),
  buyerName: z.string().nullable(),
  answerScope: QuestionnaireAnswerScopeSchema.nullable().optional(),
  dueDate: z.coerce.date().nullable(),
  /** How this questionnaire was created (#1372). EXTENSION has no file. */
  source: SecurityQuestionnaireSourceSchema,
  /** Portal page URL for EXTENSION source; null for uploads. */
  sourceUrl: z.string().nullable(),
  sourceFileName: z.string().nullable(),
  sourceMimeType: z.string().nullable(),
  status: SecurityQuestionnaireStatusSchema,
  columnMapping: QuestionnaireColumnMappingSchema.nullable(),
  errorMessage: z.string().nullable(),
  /** Derived counts for list/progress UI. */
  questionCount: z.number().int().nonnegative(),
  answeredCount: z.number().int().nonnegative(),
  createdBy: z.string(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type SecurityQuestionnaireDTO = z.infer<typeof SecurityQuestionnaireDTOSchema>;

export const SecurityQuestionnaireDetailDTOSchema = SecurityQuestionnaireDTOSchema.extend({
  questions: z.array(QuestionnaireQuestionDTOSchema),
});
export type SecurityQuestionnaireDetailDTO = z.infer<typeof SecurityQuestionnaireDetailDTOSchema>;

export const AnswerLibraryEntryDTOSchema = z.object({
  id: z.string(),
  workspaceId: z.string(),
  questionText: z.string(),
  answer: z.string(),
  citations: z.array(QuestionnaireCitationSchema).nullable(),
  category: z.string().nullable(),
  usageCount: z.number().int().nonnegative(),
  lastUsedAt: z.coerce.date().nullable(),
  approvedBy: z.string(),
  approvedAt: z.coerce.date(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type AnswerLibraryEntryDTO = z.infer<typeof AnswerLibraryEntryDTOSchema>;
